import Axios2 from "@/utils/Axios2";

/**
 * 默认配置值（与后端 EbikeChargingConfigServiceImpl 一致）
 * 注意：powerMin 已废弃，增量下限职责合并到 deltaPThreshold
 */
const DEFAULT_CONFIG = {
  enabled: true,
  deltaPThreshold: 80.0,
  powerMax: 1200.0,
  pfMin: 0.45,
  pfMax: 0.75,
  pfTolerance: 0.10,
  baselineWindowMinutes: 30,
  demoMode: false,
  suspectWindowMinutes: 5,
  clearWindowMinutes: 10,
  scoreThreshold: 2.5,
  pfcScoreThreshold: 3.0,
  inxMinThreshold: 30.0,
  inxMaxThreshold: 250.0
};

/**
 * 演示模式预设值（与后端 EbikeChargingConfig 常量一致）
 * 适用于无其他设备干扰的纯净点位，以最快速度检测
 */
const DEMO_CONFIG = {
  deltaPThreshold: 50.0,
  baselineWindowMinutes: 5,
  pfTolerance: 0.15
};

/**
 * 打开电动自行车充电识别配置弹窗
 */
export function openEbikeConfigPopup(context) {
  context.showEbikeConfigPopup = true;
  context.isEbikeConfigLoading = true;
  context.isEbikeConfigSubmitting = false;

  const branchId = context.equipmentData.branchId;
  if (!branchId) {
    context.$message.warning('未获取到回路信息，请稍后重试');
    context.showEbikeConfigPopup = false;
    context.isEbikeConfigLoading = false;
    return;
  }

  const param = {
    method: 'post',
    url: `/api/v2/ebikeChargingConfig/query/${branchId}`
  };

  Axios2.ajax(param)
    .then((res) => {
      if (res.resultCode === '0' && res.data) {
        const d = res.data;
        context.ebikeConfigForm = {
          enabled: d.enabled !== null && d.enabled !== undefined ? d.enabled : DEFAULT_CONFIG.enabled,
          deltaPThreshold: d.deltaPThreshold !== null && d.deltaPThreshold !== undefined ? d.deltaPThreshold : DEFAULT_CONFIG.deltaPThreshold,
          powerMax: d.powerMax !== null && d.powerMax !== undefined ? d.powerMax : DEFAULT_CONFIG.powerMax,
          pfMin: d.pfMin !== null && d.pfMin !== undefined ? d.pfMin : DEFAULT_CONFIG.pfMin,
          pfMax: d.pfMax !== null && d.pfMax !== undefined ? d.pfMax : DEFAULT_CONFIG.pfMax,
          pfTolerance: d.pfTolerance !== null && d.pfTolerance !== undefined ? d.pfTolerance : DEFAULT_CONFIG.pfTolerance,
          baselineWindowMinutes: d.baselineWindowMinutes !== null && d.baselineWindowMinutes !== undefined ? d.baselineWindowMinutes : DEFAULT_CONFIG.baselineWindowMinutes,
          demoMode: d.demoMode !== null && d.demoMode !== undefined ? d.demoMode : DEFAULT_CONFIG.demoMode,
          suspectWindowMinutes: d.suspectWindowMinutes !== null && d.suspectWindowMinutes !== undefined ? d.suspectWindowMinutes : DEFAULT_CONFIG.suspectWindowMinutes,
          clearWindowMinutes: d.clearWindowMinutes !== null && d.clearWindowMinutes !== undefined ? d.clearWindowMinutes : DEFAULT_CONFIG.clearWindowMinutes,
          scoreThreshold: d.scoreThreshold !== null && d.scoreThreshold !== undefined ? d.scoreThreshold : DEFAULT_CONFIG.scoreThreshold,
          pfcScoreThreshold: d.pfcScoreThreshold !== null && d.pfcScoreThreshold !== undefined ? d.pfcScoreThreshold : DEFAULT_CONFIG.pfcScoreThreshold,
          inxMinThreshold: d.inxMinThreshold !== null && d.inxMinThreshold !== undefined ? d.inxMinThreshold : DEFAULT_CONFIG.inxMinThreshold,
          inxMaxThreshold: d.inxMaxThreshold !== null && d.inxMaxThreshold !== undefined ? d.inxMaxThreshold : DEFAULT_CONFIG.inxMaxThreshold,
          minScorePoints: d.minScorePoints,
          minBaselinePointsForConfidence: d.minBaselinePointsForConfidence
        };
      } else {
        // 无记录，使用默认值
        context.ebikeConfigForm = { ...DEFAULT_CONFIG };
      }
    })
    .catch(() => {
      context.ebikeConfigForm = { ...DEFAULT_CONFIG };
    })
    .finally(() => {
      context.isEbikeConfigLoading = false;
    });
}

/**
 * 提交电动自行车充电识别配置
 */
export function submitEbikeConfig(context) {
  const form = context.ebikeConfigForm;
  const branchId = context.equipmentData.branchId;
  if (!branchId) {
    context.$message.warning('未获取到回路信息');
    return Promise.reject();
  }

  // 校验
  if (form.pfMin >= form.pfMax) {
    context.$message.warning('PF下限必须小于PF上限');
    return Promise.reject();
  }
  if (form.deltaPThreshold <= 0) {
    context.$message.warning('功率增量阈值必须大于0');
    return Promise.reject();
  }
  if (form.baselineWindowMinutes < 1) {
    context.$message.warning('基线窗口时长不能小于1分钟');
    return Promise.reject();
  }
  if (form.suspectWindowMinutes < 1) {
    context.$message.warning('SUSPECT窗口时长不能小于1分钟');
    return Promise.reject();
  }
  if (form.clearWindowMinutes < 1) {
    context.$message.warning('告警解除窗口时长不能小于1分钟');
    return Promise.reject();
  }
  if (form.scoreThreshold < 0 || form.scoreThreshold > 4.5) {
    context.$message.warning('评分阈值须在0~4.5之间');
    return Promise.reject();
  }
  if (form.pfcScoreThreshold < 0 || form.pfcScoreThreshold > 4.5) {
    context.$message.warning('PFC评分阈值须在0~4.5之间');
    return Promise.reject();
  }
  if (form.inxMinThreshold < 0) {
    context.$message.warning('ΔInx下限不能小于0');
    return Promise.reject();
  }
  if (form.inxMaxThreshold <= form.inxMinThreshold) {
    context.$message.warning('ΔInx上限必须大于下限');
    return Promise.reject();
  }
  if (form.minScorePoints < 1) {
    context.$message.warning('评分最少采样点数不能小于1');
    return Promise.reject();
  }
  if (form.minBaselinePointsForConfidence < 1) {
    context.$message.warning('基线最少数据点不能小于1');
    return Promise.reject();
  }
  // 三相设备后台自动校准，校验范围保持统一
  context.isEbikeConfigSubmitting = true;
  const param = {
    method: 'post',
    url: '/api/v2/ebikeChargingConfig/modify',
    data: {
      branchId: branchId,
      enabled: form.enabled,
      deltaPThreshold: form.deltaPThreshold,
      powerMax: form.powerMax,
      pfMin: form.pfMin,
      pfMax: form.pfMax,
      pfTolerance: form.pfTolerance,
      baselineWindowMinutes: form.baselineWindowMinutes,
      demoMode: form.demoMode,
      suspectWindowMinutes: form.suspectWindowMinutes,
      clearWindowMinutes: form.clearWindowMinutes,
      scoreThreshold: form.scoreThreshold,
      pfcScoreThreshold: form.pfcScoreThreshold,
      inxMinThreshold: form.inxMinThreshold,
      inxMaxThreshold: form.inxMaxThreshold,
      minScorePoints: form.minScorePoints,
      minBaselinePointsForConfidence: form.minBaselinePointsForConfidence
    }
  };

  return Axios2.ajax(param)
    .then((res) => {
      if (res.resultCode === '0') {
        context.$message.success('配置保存成功');
        context.showEbikeConfigPopup = false;
      } else {
        context.$message.error(res.resultMessage || '保存失败');
      }
    })
    .catch((err) => {
      context.$message.error('保存失败：' + (err.message || '未知错误'));
    })
    .finally(() => {
      context.isEbikeConfigSubmitting = false;
    });
}

/**
 * 批量修改识别开关（仅enabled/demoMode，不覆盖算法参数）
 * 用于监测点列表页的批量操作
 */
export function batchSubmitEbikeConfig(branchIds, enabled, demoMode) {
  if (!branchIds || branchIds.length === 0) {
    return Promise.reject();
  }
  const param = {
    method: 'post',
    url: '/api/v2/ebikeChargingConfig/batchModify',
    data: {
      branchIds: branchIds,
      enabled: enabled,
      demoMode: demoMode
    }
  };
  return Axios2.ajax(param);
}

/**
 * 按部门批量修改识别开关
 */
export function batchSubmitEbikeConfigByDept(deptId, enabled, demoMode) {
  if (!deptId) {
    return Promise.reject();
  }
  const param = {
    method: 'post',
    url: '/api/v2/ebikeChargingConfig/batchModifyByDept',
    data: {
      deptId: deptId,
      enabled: enabled,
      demoMode: demoMode
    }
  };
  return Axios2.ajax(param);
}

/**
 * 重置为默认配置
 */
export function resetEbikeConfig(context) {
  context.ebikeConfigForm = { ...DEFAULT_CONFIG };
  context.$message.info('已重置为默认配置');
}

/**
 * 演示模式切换回调
 * 开启时自动填充演示预设参数，关闭时恢复默认参数
 */
export function toggleDemoMode(context, value) {
  if (value) {
    // 开启演示模式：自动填充预设参数
    context.ebikeConfigForm.deltaPThreshold = DEMO_CONFIG.deltaPThreshold;
    context.ebikeConfigForm.baselineWindowMinutes = DEMO_CONFIG.baselineWindowMinutes;
    context.ebikeConfigForm.pfTolerance = DEMO_CONFIG.pfTolerance;
  } else {
    // 关闭演示模式：恢复默认参数
    context.ebikeConfigForm.deltaPThreshold = DEFAULT_CONFIG.deltaPThreshold;
    context.ebikeConfigForm.baselineWindowMinutes = DEFAULT_CONFIG.baselineWindowMinutes;
    context.ebikeConfigForm.pfTolerance = DEFAULT_CONFIG.pfTolerance;
  }
}
